/**
 * Flat list of steps across every admin page, in walkthrough order. Each step's `page` is the
 * route it belongs to — AdminTour navigates there automatically when Next/Back crosses a page
 * boundary. For steps on /admin/rules, `wizardStep` additionally tells AdminTour which page of
 * the Studio wizard to jump to first (the wizard itself never changes route), via the
 * "admin-tour-goto-wizard-step" event studio.jsx listens for.
 */
const tourSteps = [
  {
    page: "/admin/rules",
    wizardStep: 1,
    target: "studio-sidebar-steps",
    title: "The 7-step builder",
    description:
      "Every hunt is built in these 7 steps, left to right: Game basics, Setting & locations, Rounds & content, Review content, Teams, Route assignment, and Publish. A step unlocks once the one before it is complete — click any unlocked step here to jump straight to it.",
  },
  {
    page: "/admin/rules",
    wizardStep: 1,
    target: "studio-step-basics",
    title: "Game basics",
    description:
      "Name the hunt and attach the client it's for — this is what shows up on your dashboard card and at the top of every admin screen. You also pick the hunt's setting/venue vibe and its button colours, which the pre-flight check later verifies are readable (good contrast) before you're allowed to publish.",
  },
  {
    page: "/admin/rules",
    wizardStep: 2,
    target: "studio-step-locations",
    title: "Setting & locations",
    description:
      "Add every physical checkpoint teams can be sent to during the hunt — a real spot they'll have to walk to. The number of checkpoints you add here directly controls how many teams can play without two teams colliding at the same spot in the same round; fewer checkpoints than teams means some sharing is unavoidable, and the wizard will warn you about that later.",
  },
  {
    page: "/admin/rules",
    wizardStep: 3,
    target: "studio-step-rounds",
    title: "Rounds & content",
    description:
      "This is where the actual game content is written — one round per checkpoint, each with a riddle-style clue (which only leads teams TO the checkpoint), a question or task once they arrive, an answer type (text, numeric, multiple-choice, photo, video, audio, QR code, or a plain info card), and an optional hint. You can write these by hand or generate them with AI from a short brief.",
  },
  {
    page: "/admin/rules",
    wizardStep: 4,
    target: "studio-step-review",
    title: "Review content",
    description:
      "Every clue, question, answer and hint is editable here, and nothing can publish until every round is approved — this is your last chance to catch a clue that accidentally gives away the answer, or a question that doesn't fit the checkpoint. Approve rounds one at a time or use \"Approve all\" once everything looks right.",
  },
  {
    page: "/admin/rules",
    wizardStep: 5,
    target: "studio-step-teams",
    title: "Teams",
    description:
      "Set how many teams are playing and generate the join link or lobby code players use to get in. This also generates the lobby code the pre-flight check requires before you can publish.",
  },
  {
    page: "/admin/rules",
    wizardStep: 6,
    target: "studio-step-routes",
    title: "Route assignment",
    description:
      "Every team doesn't visit checkpoints in the same order — routes are rotated so team 1 might start where team 4 finishes, keeping teams spread out instead of queuing at the same spot. Routes build automatically, but you can turn on manual editing here and drag a checkpoint to a different round within one team's row if you need to fix a specific collision.",
  },
  {
    page: "/admin/rules",
    wizardStep: 7,
    target: "studio-step-publish",
    title: "Publish",
    description:
      "The final pre-flight check — every unresolved item (unapproved rounds, missing hunt name, poor colour contrast, checkpoint shortages, no lobby code) is listed here with a one-click link back to fix it. Publishing goes live immediately: it activates the hunt and releases every team currently waiting in the Waiting Room straight into the game, with no separate \"start\" step needed.",
  },
  {
    page: "/admin/rules",
    wizardStep: 7,
    target: "studio-live-host-btn",
    title: "Live Host",
    description:
      "Once a hunt is running, this is where you jump to watch and manage it in real time — team progress, stuck teams, and any submissions waiting on your approval.",
  },
  {
    page: "/admin/live",
    target: "live-stats-row",
    title: "Live overview",
    description:
      "A live snapshot of the whole hunt while it's running: how many teams are playing, total rounds completed across all of them, how many submissions are waiting on your review, how many teams have been stuck on one round for over 8 minutes, and which team is currently fastest.",
  },
  {
    page: "/admin/live",
    target: "live-settings-row",
    title: "Live settings",
    description:
      "Three switches you can flip mid-game without republishing anything: whether players can see the leaderboard, whether they can see the running timer, and \"Approve content\" — explained next.",
  },
  {
    page: "/admin/live",
    target: "live-approve-content-toggle",
    title: "Approve content",
    description:
      "Controls what happens when a team submits a photo, video, audio clip, or GIF. Off (the default) — their submission is accepted immediately and they move straight to the next checkpoint with no waiting screen. On — the current normal flow: their submission parks here for you to approve or reject before they can continue.",
  },
  {
    page: "/admin/live",
    target: "live-team-grid",
    title: "Team cards",
    description:
      "One card per team, showing their round progress, which checkpoint they're at, and how long they've been on the current round. A card needing your attention shows \"Review\" (a media submission awaiting approve/reject) or you can use \"Mark complete\" to manually push a team past their current round if they're stuck.",
  },
  {
    page: "/admin/live",
    target: "live-export-csv-btn",
    title: "Export results",
    description:
      "Downloads every team's final results as a CSV — rank, score, time, rounds completed, hints used. This only unlocks once every team has finished, so the export always reflects a complete, final leaderboard.",
  },
];

export default tourSteps;
